import { throwError as observableThrowError, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import { JsonService } from '../../services/core/json.service';
import { eventCalendarApiResources } from './event-calendar.constant';

@Injectable({
  providedIn: 'root',
})
export class EventCalendarDataService {
  private baseURL;
  constructor(private http: HttpClient, private jsonService: JsonService) {
    this.baseURL = eventCalendarApiResources;
  }

  calendar(): Observable<any> {
    const endPoint = `${this.baseURL.calendar}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createCalendar(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createCalendar}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  removeCalendar(id: any): Observable<any> {
    const endPoint = `${this.baseURL.removeCalendar}/${id}`;
    return this.http.delete<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  /**
   * GET events/{id} — single event by id, full EventResource shape (the
   * same shape EventDialogComponent already expects as its `editEvent`
   * dialog data). Used by the Global Search ?openEvent= deep link
   * (CalendarListComponent) to fetch the exact clicked event before
   * opening the existing event dialog.
   */
  getEventById(id: any): Observable<any> {
    const endPoint = `${this.baseURL.getEventById}/${id}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  calendarCategories(): Observable<any> {
    const endPoint = `${this.baseURL.calendarCategories}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getCategoriesByCalendarId(id: any): Observable<any> {
    const endPoint = `${this.baseURL.getCategoriesByCalendarId}/${id}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createCalendarCategory(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createCalendarCategory}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateCalendar(id: number, model: any): Observable<any> {
    const endPoint = `${this.baseURL.updateCalendar}/${id}`;
    return this.http.put<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateCalendarCategory(id: number, model: any): Observable<any> {
    const endPoint = `${this.baseURL.updateCalendarCategory}/${id}`;
    let params = new HttpParams();
    Object.keys(model).forEach((key) => {
      if (model[key] !== undefined && model[key] !== null) {
        params = params.set(key, model[key]);
      }
    });
    return this.http.put<any>(endPoint, null, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  deleteCalendarCategory(id: number): Observable<any> {
    const endPoint = `${this.baseURL.removeCalendarCategory}/${id}`;
    return this.http.delete<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createCalendarEvents(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createCalendarEvents}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getCalendarEvents(model: any): Observable<any> {
    const endPoint = `${this.baseURL.getCalendarEvents}`;
    return this.http.get<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getCalendarGroups(): Observable<any> {
    const endPoint = `${this.baseURL.getCalendarGroups}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  removeCalendarGroup(id: any): Observable<any> {
    const endPoint = `${this.baseURL.removeCalendarGroup}/${id}`;
    return this.http.delete<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createCalendarGroup(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createCalendarGroup}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateCalendarGroup(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.updateCalendarGroup}/${id}`;
    return this.http.put<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createCalendarDragAndDrop(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createCalendarDragAndDrop}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createEvent(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createEvent}`;
    return this.http.post<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getDashboardScheduleEvents(startDate: string, endDate: string): Observable<any> {
    const endPoint = `${this.baseURL.getDashboardScheduleEvents}`;
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getCalendarsWithEvents(calendarIds: number[], startDate?: string, endDate?: string): Observable<any> {
    const endPoint = `${this.baseURL.getCalendarsWithEvents}`;
    let params = new HttpParams();
    calendarIds.forEach((id, index) => {
      params = params.append(`calendar_ids[${index}]`, id.toString());
    });
    params = params
      .set('start_date', startDate || '2025-01-01')
      .set('end_date', endDate || '2025-12-31');
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  deleteEvent(id: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteEvent}/${id}`;
    return this.http.delete<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  deleteEventSlot(id: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteEventSlot}/${id}`;
    return this.http.delete<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateEvent(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.updateEvent}/${id}`;
    return this.http.put<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  /**
   * Reschedule a synthetic delivery event from the calendar dialog.
   * `deliveryId` is the numeric id parsed from the "delivery-N" event
   * id; `model` carries only the calendar-editable subset
   * (scheduled_date, scheduled_time?, receiving_notes?).
   */
  rescheduleDelivery(deliveryId: number, model: any): Observable<any> {
    const endPoint = `${this.baseURL.rescheduleDelivery}/${deliveryId}/reschedule`;
    return this.http.patch<any>(endPoint, model).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  shareCalendar(calendarId: number, shareData: any): Observable<any> {
    const endPoint = `${this.baseURL.shareCalendar}/share/${calendarId}`;
    return this.http.post<any>(endPoint, shareData).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getStaffListingWithSearch(queryParam: any): Observable<any> {
    const endPoint = `${this.baseURL.getStaffListingWithSearch}`;
    return this.http.get<any>(endPoint, { params: queryParam }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  moveAppointmentInCalendar(eventId: number, moveData: any): Observable<any> {
    const endPoint = `${this.baseURL.moveAppointment}/${eventId}/move`;
    moveData.loadingService = 0;
    return this.http.patch<any>(endPoint, moveData).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getModuleWithEvents(id: any, moduleId?: any, startDate?: string, endDate?: string, search?: string): Observable<any> {
    let endPoint = `${this.baseURL.getModuleWithEvents}/${id}/with/event?module_id=${moduleId}&start_date=${startDate}&end_date=${endDate}`;
    if (search && search.trim()) {
      endPoint += `&search=${encodeURIComponent(search.trim())}`;
    }
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  saveUserCalendarSettings(payload: any): Observable<any> {
    const endPoint = `${this.baseURL.userCalendarSettings}`;
    return this.http.post<any>(endPoint, payload).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}
